import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ShortlistPanel = ({ 
  shortlistedCandidates, 
  onRemoveFromShortlist, 
  onAddNote, 
  isOpen, 
  onToggle 
}) => {
  const [notes, setNotes] = useState({});
  const [editingNote, setEditingNote] = useState(null);

  const handleSaveNote = (candidateId) => {
    const note = notes?.[candidateId] || '';
    onAddNote(candidateId, note);
    setEditingNote(null);
  };

  const handleNoteChange = (candidateId, value) => {
    setNotes(prev => ({
      ...prev,
      [candidateId]: value
    }));
  };

  if (!isOpen) {
    return (
      <div className="fixed right-4 top-1/2 transform -translate-y-1/2 z-30">
        <button
          onClick={onToggle}
          className="bg-primary text-primary-foreground p-3 rounded-l-xl shadow-elevation-lg hover:bg-primary/90 transition-colors"
          aria-label="Open shortlist"
        >
          <div className="flex items-center space-x-2">
            <Icon name="Bookmark" size={20} />
            <span className="font-medium">{shortlistedCandidates?.length}</span>
          </div>
        </button>
      </div>
    );
  }

  return (
    <div className="fixed right-0 top-0 h-full w-96 bg-card border-l border-border shadow-elevation-lg z-40 overflow-hidden flex flex-col">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Shortlist</h2>
            <p className="text-sm text-muted-foreground">
              {shortlistedCandidates?.length} candidates saved
            </p>
          </div>
          <button
            onClick={onToggle}
            className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
            aria-label="Close shortlist"
          >
            <Icon name="X" size={20} />
          </button>
        </div>
      </div>
      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {shortlistedCandidates?.length === 0 ? (
          <div className="p-6 text-center">
            <div className="h-16 w-16 bg-muted/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="Bookmark" size={24} className="text-muted-foreground" />
            </div>
            <h3 className="font-medium text-foreground mb-2">No candidates shortlisted</h3>
            <p className="text-sm text-muted-foreground">
              Click the bookmark icon on candidate cards to add them to your shortlist.
            </p>
          </div>
        ) : (
          <div className="p-4 space-y-4">
            {shortlistedCandidates?.map((candidate) => (
              <div key={candidate?.id} className="bg-muted/30 rounded-lg p-4">
                {/* Candidate Info */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Icon name="User" size={16} className="text-primary" />
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground text-sm">{candidate?.title}</h4>
                      <p className="text-xs text-muted-foreground">
                        #{candidate?.id} • {candidate?.matchPercentage}% match
                      </p>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => onRemoveFromShortlist(candidate?.id)}
                    className="p-1 rounded text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                    aria-label="Remove from shortlist"
                  >
                    <Icon name="X" size={16} />
                  </button>
                </div>

                {/* Skills */}
                <div className="mb-3">
                  <div className="flex flex-wrap gap-1">
                    {candidate?.skills?.slice(0, 3)?.map((skill, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-accent/10 text-accent text-xs rounded"
                      >
                        {skill}
                      </span>
                    ))}
                    {candidate?.skills?.length > 3 && (
                      <span className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded">
                        +{candidate?.skills?.length - 3}
                      </span>
                    )}
                  </div>
                </div>

                {/* Location & Experience */}
                <div className="flex items-center space-x-4 text-xs text-muted-foreground mb-3">
                  <div className="flex items-center space-x-1">
                    <Icon name="MapPin" size={12} />
                    <span>{candidate?.location}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="Briefcase" size={12} />
                    <span>{candidate?.experienceLevel}</span>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  {editingNote === candidate?.id ? (
                    <div className="space-y-2">
                      <Input
                        type="text"
                        placeholder="Add a note about this candidate..."
                        value={notes?.[candidate?.id] || candidate?.note || ''}
                        onChange={(e) => handleNoteChange(candidate?.id, e?.target?.value)}
                        className="text-xs"
                      />
                      <div className="flex items-center space-x-2">
                        <Button
                          size="xs"
                          onClick={() => handleSaveNote(candidate?.id)}
                        >
                          Save
                        </Button>
                        <Button
                          variant="ghost"
                          size="xs"
                          onClick={() => setEditingNote(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      {candidate?.note ? (
                        <div className="bg-card rounded p-2 mb-2">
                          <p className="text-xs text-foreground">{candidate?.note}</p>
                        </div>
                      ) : null}
                      <button
                        onClick={() => setEditingNote(candidate?.id)}
                        className="flex items-center space-x-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                      >
                        <Icon name="Plus" size={12} />
                        <span>{candidate?.note ? 'Edit note' : 'Add note'}</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Footer Actions */}
      {shortlistedCandidates?.length > 0 && (
        <div className="p-4 border-t border-border space-y-3">
          <Button fullWidth variant="outline" iconName="Download">
            Export Shortlist
          </Button>
          <Button fullWidth iconName="Users">
            Share with Team
          </Button>
        </div>
      )}
    </div>
  );
};

export default ShortlistPanel;